import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, ShoppingBag } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../lib/cart';
import { absUrl } from '../lib/api';
import { formatMoney } from '../lib/utils';

/**
 * Your Basket — detailed table (Retailers-style):
 * columns: Item (image+product+flavour) · Size · Qty · Unit Price · Subtotal · Delete.
 * Below the table: total items count + Grand Total + PROCEED TO CHECKOUT.
 * Responsive: on mobile, each row collapses into a compact card layout.
 */
export default function Cart() {
    const { items, setQty, removeItem, total, count } = useCart();
    const nav = useNavigate();

    return (
        <div className="min-h-screen bg-sdf-ivory">
            <Header />
            <main className="max-w-4xl mx-auto px-3 sm:px-4 py-6 pb-24">
                <h1 className="text-2xl sm:text-3xl font-extrabold">Your Basket</h1>
                {items.length === 0 ? (
                    <div className="card-soft p-10 mt-6 text-center" data-testid="cart-empty">
                        <ShoppingBag size={48} className="mx-auto text-neutral-300" />
                        <div className="mt-3 font-bold">Your basket is empty</div>
                        <p className="text-xs text-sdf-text-2 mt-1">Start adding products from the catalogue.</p>
                        <Link to="/" className="btn-red mt-4 inline-flex">Browse Products</Link>
                    </div>
                ) : (
                    <>
                        {/* Desktop table */}
                        <div className="hidden md:block card-soft overflow-hidden mt-4">
                            <table className="w-full text-sm" data-testid="cart-table">
                                <thead className="bg-sdf-cream/60">
                                    <tr className="text-left text-[10px] font-bold text-sdf-text-2 tracking-widest">
                                        <th className="px-4 py-3">ITEM</th>
                                        <th className="px-2 py-3">SIZE</th>
                                        <th className="px-2 py-3 text-center">QUANTITY</th>
                                        <th className="px-2 py-3 text-right">UNIT PRICE</th>
                                        <th className="px-2 py-3 text-right">SUBTOTAL</th>
                                        <th className="px-2 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-sdf-line">
                                    {items.map((it) => (
                                        <tr key={`${it.product_id}-${it.variant_id}-${it.flavour_id}`} data-testid={`cart-row-${it.flavour_id}`}>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-14 h-14 rounded-xl overflow-hidden bg-neutral-100 flex-shrink-0">
                                                        {it.image_url ? <img src={absUrl(it.image_url)} alt={it.flavour_name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[10px] text-neutral-500 text-center px-1">{it.product_name}</div>}
                                                    </div>
                                                    <div>
                                                        <div className="font-bold text-sdf-text">{it.flavour_name}</div>
                                                        <div className="text-xs text-sdf-text-2">{it.product_name}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-2 py-3 font-semibold">{it.size_label}</td>
                                            <td className="px-2 py-3">
                                                <div className="flex items-center gap-2 justify-center">
                                                    <button onClick={() => setQty(it.product_id, it.variant_id, it.flavour_id, it.quantity - 1)} className="w-8 h-8 rounded-full bg-neutral-200 hover:bg-neutral-300 flex items-center justify-center" aria-label="Decrease"><Minus size={14} /></button>
                                                    <span className="font-bold text-sm min-w-[28px] text-center">{it.quantity}</span>
                                                    <button onClick={() => setQty(it.product_id, it.variant_id, it.flavour_id, it.quantity + 1)} className="w-8 h-8 rounded-full bg-sdf-red text-white hover:bg-sdf-red-hover flex items-center justify-center" aria-label="Increase"><Plus size={14} /></button>
                                                </div>
                                            </td>
                                            <td className="px-2 py-3 text-right font-semibold">{formatMoney(it.price)}</td>
                                            <td className="px-2 py-3 text-right font-extrabold">{formatMoney(it.price * it.quantity)}</td>
                                            <td className="px-2 py-3 text-right">
                                                <button onClick={() => removeItem(it.product_id, it.variant_id, it.flavour_id)} className="p-2 text-neutral-500 hover:text-red-600" aria-label="Remove" data-testid={`remove-${it.flavour_id}`}><Trash2 size={16} /></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile card list */}
                        <div className="md:hidden mt-4 space-y-3">
                            {items.map((it) => (
                                <div key={`${it.product_id}-${it.variant_id}-${it.flavour_id}`} className="card-soft p-3" data-testid={`cart-card-${it.flavour_id}`}>
                                    <div className="flex items-center gap-3">
                                        <div className="w-16 h-16 rounded-xl overflow-hidden bg-neutral-100 flex items-center justify-center flex-shrink-0">
                                            {it.image_url ? <img src={absUrl(it.image_url)} alt={it.flavour_name} className="w-full h-full object-cover" /> : <div className="text-[10px] text-neutral-500 text-center px-1">{it.product_name}</div>}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-sm text-sdf-text line-clamp-1">{it.flavour_name}</div>
                                            <div className="text-xs text-sdf-text-2">{it.product_name} · {it.size_label}</div>
                                            <div className="text-xs text-sdf-text-2 mt-0.5">Unit {formatMoney(it.price)}</div>
                                        </div>
                                        <button onClick={() => removeItem(it.product_id, it.variant_id, it.flavour_id)} className="p-2 text-neutral-500 hover:text-red-600" data-testid={`remove-${it.flavour_id}`}><Trash2 size={16} /></button>
                                    </div>
                                    <div className="mt-3 flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <button onClick={() => setQty(it.product_id, it.variant_id, it.flavour_id, it.quantity - 1)} className="w-8 h-8 rounded-full bg-neutral-200 hover:bg-neutral-300 flex items-center justify-center"><Minus size={14} /></button>
                                            <span className="font-bold text-sm min-w-[28px] text-center">{it.quantity}</span>
                                            <button onClick={() => setQty(it.product_id, it.variant_id, it.flavour_id, it.quantity + 1)} className="w-8 h-8 rounded-full bg-sdf-red text-white hover:bg-sdf-red-hover flex items-center justify-center"><Plus size={14} /></button>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-[10px] font-bold uppercase tracking-widest text-sdf-text-2">SUBTOTAL</div>
                                            <div className="font-extrabold">{formatMoney(it.price * it.quantity)}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Totals */}
                        <div className="card-soft p-4 mt-4">
                            <div className="text-sdf-red font-bold text-xs tracking-widest">ORDER SUMMARY</div>
                            <div className="mt-3 flex items-center justify-between text-sm">
                                <div className="text-sdf-text-2">Total items</div>
                                <div className="font-semibold" data-testid="cart-count">{count}</div>
                            </div>
                            <div className="flex items-center justify-between mt-2 pt-3 border-t border-sdf-line">
                                <div className="font-bold">Grand Total</div>
                                <div className="font-extrabold text-lg" data-testid="cart-total">{formatMoney(total)}</div>
                            </div>
                        </div>
                        <button onClick={() => nav('/checkout')} className="btn-red-lg mt-4" data-testid="proceed-checkout">PROCEED TO CHECKOUT</button>
                    </>
                )}
            </main>
            <Footer />
        </div>
    );
}
